import React from 'react';
import { cn } from '../../utils/helpers';

const Card = React.forwardRef(({ 
  className, 
  children, 
  variant = 'default',
  size = 'default',
  hover = false,
  shadow = true,
  border = true,
  padding = true,
  ...props 
}, ref) => {
  const baseStyles = 'rounded-2xl bg-white/95 backdrop-blur-sm transition-all duration-200 dark:bg-slate-950/88';
  
  const variants = {
    default: 'border-gray-200 dark:border-gray-700',
    primary: 'border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20',
    success: 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20',
    warning: 'border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20',
    danger: 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20',
    ghost: 'border-transparent bg-transparent'
  };

  const sizes = {
    sm: 'text-sm',
    default: 'text-base',
    lg: 'text-lg'
  };

  const shadowStyles = {
    true: 'shadow-sm shadow-slate-900/5 hover:shadow-lg hover:shadow-slate-900/10 dark:shadow-black/20',
    false: 'shadow-none'
  };

  const hoverStyles = {
    true: 'hover:-translate-y-0.5 cursor-pointer',
    false: ''
  };

  const borderStyles = {
    true: 'border',
    false: 'border-0'
  };

  const paddingStyles = {
    true: 'p-4 sm:p-5 lg:p-6',
    false: 'p-0'
  };

  return (
    <div
      ref={ref}
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        shadowStyles[shadow],
        hoverStyles[hover],
        borderStyles[border],
        paddingStyles[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

const CardHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 pb-4 sm:pb-5', className)}
    {...props}
  >
    {children}
  </div>
));

CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-xl',
      className
    )}
    {...props}
  >
    {children}
  </h3>
));

CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef(({ className, children, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm leading-6 text-gray-600 dark:text-gray-400', className)}
    {...props}
  >
    {children}
  </p>
));

CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn('pt-0', className)} {...props}>
    {children}
  </div>
));

CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center pt-4 sm:pt-5', className)}
    {...props}
  >
    {children}
  </div>
));

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
